import { useEffect, useState, useMemo } from "react";
import api from "../api";

export default function PointsHistory({ userId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day'); // day|week|month|year|all
  const [refDate, setRefDate] = useState(() => new Date()); // base date selection
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Derived strings for inputs
  const dayInputValue = useMemo(() => {
    const d = refDate; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  }, [refDate]);
  const monthInputValue = useMemo(() => {
    const d = refDate; return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  }, [refDate]);
  const yearValue = useMemo(() => refDate.getFullYear(), [refDate]);

  function setDayFromInput(v) {
    try { const [y,m,dd] = v.split('-').map(Number); if (y && m) setRefDate(new Date(y, m-1, dd||1)); } catch {}
  }
  function setMonthFromInput(v) {
    try { const [y,m] = v.split('-').map(Number); if (y && m) setRefDate(new Date(y, m-1, 1)); } catch {}
  }
  function setYearFromInput(y) {
    try { const yr = Number(y); if (yr) setRefDate(new Date(yr, 0, 1)); } catch {}
  }

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true); setError('');
      const endpoint = userId ? `/admin/user/${userId}/pointlog` : `/admin/user/pointlog`;
      const params = new URLSearchParams();
      params.set('period', period);
      if (period !== 'all') params.set('refDate', refDate.toISOString());
      params.set('page', String(page));
      params.set('limit', String(limit));
      try {
        const res = await api.get(endpoint + '?' + params.toString());
        if (!cancel) {
          const items = Array.isArray(res.data?.items) ? res.data.items : (Array.isArray(res.data) ? res.data : []);
          setLogs(items);
          if (typeof res.data?.page === 'number') setPage(res.data.page);
          if (typeof res.data?.pages === 'number') setPages(res.data.pages);
          if (typeof res.data?.total === 'number') setTotal(res.data.total);
          if (typeof res.data?.limit === 'number') setLimit(res.data.limit);
        }
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.error || 'Fehler beim Laden der Punktehistorie');
      } finally { if (!cancel) setLoading(false); }
    }
    load();
    return () => { cancel = true; };
  }, [userId, period, refDate, page, limit]);

  // Reset page when period or reference date changes
  useEffect(() => { setPage(1); }, [period, refDate]);

  // Week label (Monday-Sunday)
  const weekRangeLabel = useMemo(() => {
    if (period !== 'week') return '';
    const base = refDate;
    const dayOfWeek = (base.getDay() + 6) % 7; // Monday=0
    const start = new Date(base.getFullYear(), base.getMonth(), base.getDate() - dayOfWeek);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    const fmt = d => `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
    return `${fmt(start)} – ${fmt(end)}`;
  }, [period, refDate]);

  return (
    <div style={{padding:16}}>
      <h2 style={{marginBottom:12}}>Deine Punktehistorie</h2>
      <HistoryFilters
        period={period}
        onChangePeriod={setPeriod}
        refDate={refDate}
        dayInputValue={dayInputValue}
        monthInputValue={monthInputValue}
        yearValue={yearValue}
        setDayFromInput={setDayFromInput}
        setMonthFromInput={setMonthFromInput}
        setYearFromInput={setYearFromInput}
        setRefDate={setRefDate}
        weekRangeLabel={weekRangeLabel}
        page={page}
        pages={pages}
        total={total}
        limit={limit}
        setPage={setPage}
        setLimit={setLimit}
        periodHasPagination={true}
        logs={logs}
        userId={userId}
      />
      {loading && <div style={{marginTop:12}}>Lade Punktehistorie …</div>}
      {!loading && error && <div style={{marginTop:12, color:'#f87171'}}>{error}</div>}
      {!loading && !error && logs.length === 0 && <div style={{marginTop:12}}>Keine Einträge für ausgewählten Zeitraum.</div>}
      {!loading && !error && logs.length > 0 && (
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:14, marginTop:12}}>
          <thead>
            <tr style={{background:'#1d2732',color:'#32CD89'}}>
              <th style={{padding:8,textAlign:'left'}}>Datum</th>
              <th style={{padding:8,textAlign:'right'}}>Punkte</th>
              <th style={{padding:8,textAlign:'left'}}>Grund</th>
              <th style={{padding:8,textAlign:'left'}}>Quelle</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id} style={{borderBottom:'1px solid #243546'}}>
                <td style={{padding:8, whiteSpace:'nowrap'}}>{new Date(log.createdAt).toLocaleString('de-DE')}</td>
                <td style={{padding:8, textAlign:'right', fontWeight:600, color: log.amount > 0 ? '#32CD89' : '#f87171'}}>{log.amount}</td>
                <td style={{padding:8}}>{log.reason}</td>
                <td style={{padding:8}}>{log.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !error && logs.length > 0 && (
        <SummaryBar logs={logs} total={total} period={period} />
      )}
    </div>
  );
}

function HistoryFilters({ period, onChangePeriod, refDate, dayInputValue, monthInputValue, yearValue, setDayFromInput, setMonthFromInput, setYearFromInput, setRefDate, weekRangeLabel, page, pages, total, limit, setPage, setLimit, periodHasPagination, logs, userId }) {
  return (
    <div style={{display:'flex', flexDirection:'column', gap:12, background:'#0f1e2d', border:'1px solid #1e3347', borderRadius:12, padding:'12px 14px'}}>
      <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
        {['day','week','month','year','all'].map(p => (
          <button
            key={p}
            type="button"
            onClick={() => onChangePeriod(p)}
            style={{
              padding:'6px 14px',
              fontSize:12,
              borderRadius:999,
              border:'1px solid ' + (period === p ? '#32CD89' : '#334155'),
              background: period === p ? 'rgba(50,205,137,0.15)' : 'transparent',
              color: period === p ? '#32CD89' : '#cbd5e1',
              cursor:'pointer'
            }}
          >{labelForPeriod(p)}</button>
        ))}
      </div>
      {period === 'day' && (
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <label style={{fontSize:12, opacity:.8}}>Tag wählen:</label>
          <input type="date" value={dayInputValue} onChange={e => setDayFromInput(e.target.value)} style={inputStyle()} />
          <button type="button" onClick={() => setRefDate(new Date())} style={smallBtnStyle()}>Heute</button>
        </div>
      )}
      {period === 'week' && (
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <label style={{fontSize:12, opacity:.8}}>Datum innerhalb Woche:</label>
          <input type="date" value={dayInputValue} onChange={e => setDayFromInput(e.target.value)} style={inputStyle()} />
          <button type="button" onClick={() => setRefDate(new Date())} style={smallBtnStyle()}>Aktuelle Woche</button>
          <span style={{fontSize:12, color:'#93c5fd'}}>{weekRangeLabel}</span>
        </div>
      )}
      {period === 'month' && (
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <label style={{fontSize:12, opacity:.8}}>Monat:</label>
          <input type="month" value={monthInputValue} onChange={e => setMonthFromInput(e.target.value)} style={inputStyle()} />
          <button type="button" onClick={() => setRefDate(new Date())} style={smallBtnStyle()}>Aktueller Monat</button>
        </div>
      )}
      {period === 'year' && (
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap'}}>
          <label style={{fontSize:12, opacity:.8}}>Jahr:</label>
          <select value={yearValue} onChange={e => setYearFromInput(e.target.value)} style={inputStyle()}>
            {yearOptions(yearValue).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button type="button" onClick={() => setRefDate(new Date())} style={smallBtnStyle()}>Aktuelles Jahr</button>
        </div>
      )}
      {period === 'all' && (
        <div style={{fontSize:12, opacity:.8}}>Alle verfügbaren Einträge.</div>
      )}
      {/* Pagination controls */}
      {periodHasPagination && (
        <div style={{display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginTop:4}}>
          <div style={{fontSize:12, opacity:.7}}>Gesamt: {total}</div>
          <div style={{display:'flex', gap:6}}>
            <button type="button" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page<=1} style={pageBtnStyle(page<=1)}>« Prev</button>
            <span style={{fontSize:12, padding:'4px 8px', background:'#1e2d3b', borderRadius:6}}>Seite {page}/{pages}</span>
            <button type="button" onClick={() => setPage(p => Math.min(pages, p+1))} disabled={page>=pages} style={pageBtnStyle(page>=pages)}>Next »</button>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:6}}>
            <label style={{fontSize:12, opacity:.7}}>Limit:</label>
            <select value={String(limit)} onChange={e => { const v = e.target.value; setLimit(v === 'all' ? 'all' : Number(v)); setPage(1); }} style={inputStyle()}>
              {[25,50,100,250,500,1000].map(l => <option key={l} value={String(l)}>{l}</option>)}
              <option value="all">Alle</option>
            </select>
          </div>
          <ExportButtons logs={logs} period={period} refDate={refDate} limit={limit} total={total} userId={userId} />
        </div>
      )}
    </div>
  );
}

function labelForPeriod(p) {
  switch(p) {
    case 'day': return 'Tag';
    case 'week': return 'Woche';
    case 'month': return 'Monat';
    case 'year': return 'Jahr';
    case 'all': return 'All-Time';
    default: return p;
  }
}
function inputStyle() {
  return { padding:'6px 10px', fontSize:12, borderRadius:8, border:'1px solid #334155', background:'#0d1b29', color:'#e2e8f0' };
}
function smallBtnStyle() {
  return { padding:'6px 10px', fontSize:11, borderRadius:8, border:'1px solid #334155', background:'rgba(51,65,85,0.3)', color:'#cbd5e1', cursor:'pointer' };
}
function yearOptions(currentYear) {
  const list = [];
  const now = new Date().getFullYear();
  for (let y = now; y >= now - 10; y--) list.push(y);
  if (!list.includes(currentYear)) list.push(currentYear);
  return list.sort((a,b)=>b-a);
}
function pageBtnStyle(disabled) {
  return { padding:'6px 12px', fontSize:11, borderRadius:8, border:'1px solid '+(disabled?'#334155':'#556b84'), background: disabled?'#1e2d3b':'#2b4258', color: disabled?'#64748b':'#e2e8f0', cursor: disabled?'default':'pointer' };
}

function SummaryBar({ logs, total, period }) {
  const sum = logs.reduce((acc,l)=> acc + (Number(l.amount)||0), 0);
  const positives = logs.filter(l => (l.amount||0) > 0).reduce((a,l)=> a + l.amount, 0);
  const negatives = logs.filter(l => (l.amount||0) < 0).reduce((a,l)=> a + l.amount, 0);
  return (
    <div style={{marginTop:14, background:'#102536', border:'1px solid #1e3347', borderRadius:10, padding:'10px 14px', display:'flex', flexWrap:'wrap', gap:12, fontSize:12}}>
      <div><strong>Zeitraum:</strong> {labelForPeriod(period)}</div>
      <div><strong>Einträge:</strong> {logs.length} / {total}</div>
      <div><strong>Summe:</strong> <span style={{color: sum>=0 ? '#32CD89':'#f87171', fontWeight:600}}>{sum}</span></div>
      <div><strong>+ Punkte:</strong> <span style={{color:'#32CD89'}}>{positives}</span></div>
      <div><strong>- Punkte:</strong> <span style={{color:'#f87171'}}>{negatives}</span></div>
    </div>
  );
}

function ExportButtons({ logs, period, refDate, limit, total, userId }) {
  async function exportCurrentPage() {
    if (!logs || logs.length === 0) return;
    const csv = buildCsv(logs);
    triggerDownload(csv, `punkte_${period}_page.csv`);
  }
  async function exportAllFiltered() {
    // Progressive fetch of all pages with current filter; caution if very large.
    try {
      const endpoint = userId ? `/admin/user/${userId}/pointlog` : `/admin/user/pointlog`;
      const baseParams = new URLSearchParams();
      baseParams.set('period', period);
      if (period !== 'all') baseParams.set('refDate', refDate.toISOString());
      baseParams.set('limit', '500'); // large chunk size
      let page = 1;
      let all = [];
      while (true) {
        const params = new URLSearchParams(baseParams.toString());
        params.set('page', String(page));
        const res = await api.get(endpoint + '?' + params.toString());
        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        all = all.concat(items);
        const pages = res.data?.pages || 1;
        if (page >= pages) break;
        page++;
      }
      const csv = buildCsv(all);
      triggerDownload(csv, `punkte_${period}_alle.csv`);
    } catch (e) {
      console.error('CSV Export fehlgeschlagen', e);
    }
  }
  return (
    <div style={{display:'flex', gap:6}}>
      <button type="button" onClick={exportCurrentPage} style={smallBtnStyle()}>CSV Seite</button>
      <button type="button" onClick={exportAllFiltered} style={smallBtnStyle()}>CSV Zeitraum</button>
    </div>
  );
}

function buildCsv(items) {
  const header = ['ID','Datum','Punkte','Grund','Quelle'];
  const rows = items.map(l => [l.id, new Date(l.createdAt).toISOString(), l.amount, safeCsv(l.reason), safeCsv(l.source)]);
  return [header.join(';'), ...rows.map(r => r.join(';'))].join('\n');
}
function safeCsv(v) { if (v == null) return ''; return String(v).replace(/[\r\n]+/g,' ').replace(/;/g, ','); }
function triggerDownload(content, filename) {
  const blob = new Blob([content], { type:'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.style.display='none';
  document.body.appendChild(a); a.click();
  setTimeout(()=> { URL.revokeObjectURL(url); a.remove(); }, 1000);
}
